package com.flowyride.core.domain.gpx

import com.flowyride.core.domain.model.GeoPoint
import com.flowyride.core.domain.model.Route
import kotlin.math.abs
import kotlin.math.roundToLong

/** A named point shown on top of the track (departure, stop, arrival). */
data class GpxWaypoint(val name: String, val point: GeoPoint)

/**
 * Serialises a [Route] to GPX 1.1: stops as waypoints (`wpt`) and the ride as a single track (`trk`).
 *
 * Tracks are understood by virtually every GPS and navigation app; waypoints make stops visible there.
 */
class GpxWriter(private val creator: String = "FlowyRide") {

    fun write(route: Route, name: String, waypoints: List<GpxWaypoint> = emptyList()): String = buildString {
        appendLine("""<?xml version="1.0" encoding="UTF-8"?>""")
        appendLine(
            """<gpx version="1.1" creator="${creator.xmlEscaped()}" """ +
                """xmlns="http://www.topografix.com/GPX/1/1" """ +
                """xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" """ +
                """xsi:schemaLocation="http://www.topografix.com/GPX/1/1 http://www.topografix.com/GPX/1/1/gpx.xsd">""",
        )
        appendLine("  <metadata>")
        appendLine("    <name>${name.xmlEscaped()}</name>")
        appendLine("  </metadata>")
        waypoints.forEach { waypoint ->
            val lat = waypoint.point.latitude.toFixed(COORDINATE_DECIMALS)
            val lon = waypoint.point.longitude.toFixed(COORDINATE_DECIMALS)
            appendLine("""  <wpt lat="$lat" lon="$lon"><name>${waypoint.name.xmlEscaped()}</name></wpt>""")
        }
        appendLine("  <trk>")
        appendLine("    <name>${name.xmlEscaped()}</name>")
        appendLine("    <trkseg>")
        route.track.forEach { trackPoint ->
            val lat = trackPoint.point.latitude.toFixed(COORDINATE_DECIMALS)
            val lon = trackPoint.point.longitude.toFixed(COORDINATE_DECIMALS)
            val elevation = trackPoint.elevationMeters
            if (elevation == null) {
                appendLine("""      <trkpt lat="$lat" lon="$lon"/>""")
            } else {
                appendLine("""      <trkpt lat="$lat" lon="$lon"><ele>${elevation.toFixed(ELEVATION_DECIMALS)}</ele></trkpt>""")
            }
        }
        appendLine("    </trkseg>")
        appendLine("  </trk>")
        append("</gpx>")
    }

    private companion object {
        /** 6 decimals ≈ 11 cm, more than enough for a road. */
        const val COORDINATE_DECIMALS = 6
        const val ELEVATION_DECIMALS = 1
    }
}

internal fun String.xmlEscaped(): String = buildString(length) {
    for (c in this@xmlEscaped) {
        when (c) {
            '&' -> append("&amp;")
            '<' -> append("&lt;")
            '>' -> append("&gt;")
            '"' -> append("&quot;")
            '\'' -> append("&apos;")
            else -> append(c)
        }
    }
}

/** Locale-independent fixed-point formatting, identical on every platform (no scientific notation). */
internal fun Double.toFixed(decimals: Int): String {
    var factor = 1L
    repeat(decimals) { factor *= 10 }
    val scaled = (abs(this) * factor).roundToLong()
    val sign = if (this < 0 && scaled != 0L) "-" else ""
    val integerPart = scaled / factor
    if (decimals == 0) return "$sign$integerPart"
    val fractionPart = (scaled % factor).toString().padStart(decimals, '0')
    return "$sign$integerPart.$fractionPart"
}
